import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import Icon from "../Icon";
import { Input } from "../Input";

export interface OptionType {
  value: string | number | null;
  label: string;
}

export type Props = {
  value?: string;
  name?: string;
  placeholder?: string;
  error?: string;
  note?: string;
  success?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  viewOnly?: boolean;
  className?: string;
  autoComplete?: boolean;
  options: OptionType[];
  postfix?: ReactNode;
  dataTest?: string;
};

export const Select = forwardRef<HTMLDivElement, Props>(
  (
    {
      autoComplete = false,
      value = "",
      name = "",
      placeholder = "",
      error = "",
      note = "",
      success = false,
      className = "",
      onChange,
      onBlur = (_?: ChangeEvent<HTMLInputElement>): void => undefined,
      disabled = false,
      viewOnly = false,
      options = [],
      postfix,
      dataTest,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<OptionType | undefined>();
    const innerRef = useRef<HTMLDivElement>(null);
    const [text, setText] = useState("");

    useEffect(() => {
      if (value) {
        setSelected(options.find((option) => option.value === value));
      } else {
        setSelected(undefined);
      }
    }, [value, options]);

    useImperativeHandle(ref, () => innerRef.current!, []);

    const onType = (e: ChangeEvent<HTMLInputElement>) => {
      if (selected) {
        setSelected(undefined);
      }

      setText(e.target.value);
    };

    useEffect(() => {}, [text]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (innerRef?.current && !innerRef.current.contains(e.target as Node)) {
          setIsOpen(false);

          const event = {
            target: {
              value: selected?.value,
              name,
            },
          };

          onBlur(event as ChangeEvent<HTMLInputElement>);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [name, onBlur, selected]);

    const handleSelect = (option: OptionType) => {
      const event = {
        target: {
          value: option.value,
          name,
        },
      };

      onChange(event as ChangeEvent<HTMLInputElement>);
      setSelected(option);
      setText("");
      setIsOpen(false);
    };

    return (
      <div
        ref={innerRef}
        className={classNames("relative", className)}
        data-test={dataTest}
      >
        <Input
          className={classNames({ "cursor-pointer": !autoComplete })}
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || viewOnly}
          viewOnly={!isOpen || !autoComplete}
          placeholder={placeholder}
          value={selected?.label || text}
          onChange={onType}
          postfix={
            postfix
              ? postfix
              : !disabled &&
                !viewOnly && (
                  <span className="select-chevron">
                    <Icon
                      icon="Chevron-down"
                      size={24}
                      className="text-gray-500"
                      disableFill
                    />
                  </span>
                )
          }
          error={error}
          note={note}
          dataTest={`${dataTest}-input`}
        />

        {isOpen && (
          <ul
            className="select-dropdown bg-white mt-2 rounded-md z-[60] overflow-y-scroll"
            data-test={`${dataTest}-dropdown`}
          >
            {options
              .filter(
                (option) =>
                  !text ||
                  option.label
                    .toLowerCase()
                    .startsWith(text.toLocaleLowerCase())
              )
              .map((option) => (
                <li
                  className="select-dropdown-item"
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  data-test={`${dataTest}-dropdown-item-${option.value}`}
                >
                  {option.label}
                </li>
              ))}
          </ul>
        )}
      </div>
    );
  }
);
